import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaWhatsapp, FaPhone, FaEnvelope, FaClock,
  FaArrowRight, FaCheckCircle, FaExclamationTriangle,
  FaUserMd, FaInstagram, FaLinkedin, FaYoutube,
  FaCommentDots, FaFlask, FaQuestionCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const PHONE = '+91 9579520586';
const WA_LINK = 'https://wa.me/919579520586';
const EMAIL = 'glp1@fitnession.fit';

const topics = [
  'Eligibility query',
  'Book a consultation',
  'Medication question',
  'Side effect concern',
  'Lab test / results',
  'Billing or payment',
  'Prescription / delivery',
  'Program cancellation',
  'Other',
];

const quickLinks = [
  { icon: FaCheckCircle, label: 'Check Eligibility', to: '/eligibility', color: 'text-gold-500' },

  { icon: FaFlask, label: 'Research & Science', to: '/research', color: 'text-purple-500' },

];

const GLP1Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', topic: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.topic) e.topic = 'Please select a topic';
    if (!form.message.trim()) e.message = 'Message is required';
    else if (form.message.trim().length < 20) e.message = 'Please write at least 20 characters';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);

    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || 'Not provided'}\n\n${form.message}`
    );
    window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(`[${form.topic}] from ${form.name}`)}&body=${body}`;

    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      toast.success('Message prepared — your email client should open shortly.');
    }, 800);
  };

  return (
    <div className="bg-white min-h-screen">

      {/* Emergency strip */}
      <div className="bg-red-600 text-white py-2.5 px-4 text-center text-sm font-semibold">
        <FaExclamationTriangle className="inline mr-2 text-yellow-300" />
        Medical emergency?{' '}
        <a href="tel:112" className="underline font-black">Call 112</a>
        {' '}or go to the nearest emergency room. Do NOT use this form.
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center bg-gold-500/20 border border-gold-500/40 rounded-full px-4 py-1.5 mb-5">
            <span className="w-2 h-2 rounded-full bg-gold-400 mr-2 animate-pulse" />
            <span className="text-gold-400 text-sm font-semibold uppercase tracking-wider">GLP-1 Medical Support</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Talk to Our
            <span className="block text-gold-400">Medical Team</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-xl mx-auto">
            Eligibility questions, medication queries, billing support — our team responds within 2 hours on weekdays.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-3 gap-12">

          {/* ── Left: Contact Info ──────────────────────────────────── */}
          <div className="space-y-5">

            {/* Contact method cards */}
            {[
              {
                icon: FaWhatsapp,
                label: 'WhatsApp',
                value: PHONE,
                sub: 'Fastest — usually within 30 min',
                href: WA_LINK,
                iconBg: 'bg-green-50',
                iconColor: 'text-green-600',
                border: 'border-green-100',
                badge: 'Recommended',
                badgeColor: 'bg-green-100 text-green-700',
              },
              {
                icon: FaPhone,
                label: 'Call Us',
                value: PHONE,
                sub: 'Mon – Sat · 9 am – 7 pm IST',
                href: `tel:+919579520586`,
                iconBg: 'bg-blue-50',
                iconColor: 'text-blue-600',
                border: 'border-blue-100',
              },
              {
                icon: FaEnvelope,
                label: 'Email',
                value: EMAIL,
                sub: 'Reply within 4 business hours',
                href: `mailto:${EMAIL}`,
                iconBg: 'bg-gold-50',
                iconColor: 'text-gold-600',
                border: 'border-gold-100',
              },
            ].map((c, i) => (
              <a
                key={i}
                href={c.href}
                target={c.href.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                className={`flex items-start gap-4 p-4 bg-white border ${c.border} rounded-2xl hover:shadow-md transition-shadow group`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${c.iconBg} group-hover:scale-110 transition-transform`}>
                  <c.icon className={`text-xl ${c.iconColor}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{c.label}</p>
                    {c.badge && <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.badgeColor}`}>{c.badge}</span>}
                  </div>
                  <p className="font-semibold text-gray-900 text-sm">{c.value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{c.sub}</p>
                </div>
              </a>
            ))}

            {/* Hours */}
            <div className="bg-navy-50 border border-navy-100 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <FaClock className="text-navy-600" />
                <p className="font-semibold text-navy-900 text-sm">Support Hours</p>
              </div>
              <div className="space-y-1.5 text-sm">
                {[
                  ['Mon – Fri', '9:00 am – 7:00 pm IST'],
                  ['Saturday', '10:00 am – 5:00 pm IST'],
                  ['Sunday', 'WhatsApp only'],
                  ['Medical emergency', 'Call 112 anytime'],
                ].map(([day, hours], i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-gray-600">{day}</span>
                    <span className={`font-medium ${i === 3 ? 'text-red-600' : 'text-navy-900'}`}>{hours}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick links */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Quick Links</p>
              <div className="space-y-1">
                {quickLinks.map((l, i) => (
                  l.to ? (
                    <Link key={i} to={l.to} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 group transition-colors">
                      <l.icon className={`${l.color} text-sm flex-shrink-0`} />
                      <span className="text-sm text-gray-700 group-hover:text-navy-900 flex-1">{l.label}</span>
                      <FaArrowRight className="text-xs text-gray-300 group-hover:text-gold-500 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  ) : (
                    <a key={i} href={l.href} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 group transition-colors">
                      <l.icon className={`${l.color} text-sm flex-shrink-0`} />
                      <span className="text-sm text-gray-700 group-hover:text-navy-900 flex-1">{l.label}</span>
                      <FaArrowRight className="text-xs text-gray-300 group-hover:text-gold-500 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  )
                ))}
              </div>
            </div>

            {/* Social */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Follow Us</p>
              <div className="flex gap-3">
                {[
                  { icon: FaInstagram, label: 'Instagram' },
                  { icon: FaLinkedin, label: 'LinkedIn' },
                  { icon: FaYoutube, label: 'YouTube' },
                ].map((s, i) => (
                  <a key={i} href="#" aria-label={s.label} className="w-10 h-10 bg-gray-100 hover:bg-navy-800 hover:text-white rounded-xl flex items-center justify-center text-gray-500 transition-colors">
                    <s.icon />
                  </a>
                ))}
              </div>
            </div>

          </div>

          {/* ── Right: Form ─────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Enrolled patient note */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3">
              <FaUserMd className="text-blue-500 text-lg flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 text-sm mb-0.5">Already enrolled?</p>
                <p className="text-blue-700 text-xs leading-relaxed">
                  For weekly check-ins, lab results, and direct doctor messages, please use your{' '}
                  <strong>programme dashboard</strong> or WhatsApp your assigned doctor directly.
                  This form is for new enquiries only.
                </p>
              </div>
            </div>

            {/* Form card */}
            <div className="bg-white border border-gray-100 rounded-3xl shadow-lg p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <FaCheckCircle className="text-green-500 text-4xl" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h2>
                  <p className="text-gray-500 mb-2">Your email client should have opened with your message pre-filled.</p>
                  <p className="text-gray-500 text-sm mb-8">
                    We'll reply to <span className="font-semibold text-gray-800">{form.email}</span> within 4 business hours.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => { setForm({ name: '', email: '', phone: '', topic: '', message: '' }); setErrors({}); setSubmitted(false); }}
                      className="px-6 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors"
                    >
                      Send Another
                    </button>
                    <Link to="/eligibility" className="px-6 py-2.5 bg-gold-500 hover:bg-gold-600 text-white rounded-xl font-bold text-sm transition-colors text-center">
                      Check Eligibility
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-7">
                    <div className="flex items-center gap-2 mb-1">
                      <FaCommentDots className="text-gold-500" />
                      <h2 className="text-2xl font-bold text-gray-900">Send Us a Message</h2>
                    </div>
                    <p className="text-gray-500 text-sm">We typically respond within 2 hours on weekdays.</p>
                  </div>

                  <form onSubmit={handleSubmit} noValidate className="space-y-5">

                    {/* Name + Email */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Priya Sharma"
                          className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 transition-colors ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-gold-400'}`}
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="priya@example.com"
                          className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 transition-colors ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-gold-400'}`}
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    {/* Phone + Topic */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Phone <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <div className="flex">
                          <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-gray-200 bg-gray-50 text-gray-500 text-sm">+91</span>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="98765 43210"
                            className="flex-1 px-4 py-3 rounded-r-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-gold-400 transition-colors"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Topic <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="topic"
                          value={form.topic}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-xl border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gold-400 transition-colors ${errors.topic ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-gold-400'}`}
                        >
                          <option value="">Select a topic…</option>
                          {topics.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        {errors.topic && <p className="text-red-500 text-xs mt-1">{errors.topic}</p>}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Describe your query — the more detail you share, the better we can help…"
                        className={`w-full px-4 py-3 rounded-xl border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gold-400 transition-colors ${errors.message ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-gold-400'}`}
                      />
                      <div className="flex justify-between mt-1">
                        {errors.message
                          ? <p className="text-red-500 text-xs">{errors.message}</p>
                          : <span />
                        }
                        <p className={`text-xs ${form.message.length < 20 ? 'text-gray-400' : 'text-green-500'}`}>
                          {form.message.length} / 20 min
                        </p>
                      </div>
                    </div>

                    {/* Submit */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-600 disabled:bg-gold-300 text-white px-8 py-3.5 rounded-xl font-bold text-sm transition-all shadow-lg hover:shadow-gold-500/30 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:translate-y-0"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            Sending…
                          </>
                        ) : (
                          <>Send Message <FaArrowRight className="ml-2" /></>
                        )}
                      </button>
                      <p className="text-xs text-gray-400">
                        Protected by our{' '}
                        <Link to="/privacy-policy" className="underline hover:text-gray-600">Privacy Policy</Link>
                      </p>
                    </div>

                  </form>
                </>
              )}
            </div>

            {/* Eligibility CTA */}
            <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-6 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-bold mb-1">Ready to check if you qualify?</p>
                <p className="text-gray-300 text-sm">Free 5-minute assessment — no commitment required.</p>
              </div>
              <Link
                to="/eligibility"
                className="flex-shrink-0 inline-flex items-center bg-gold-500 hover:bg-gold-600 text-white px-6 py-3 rounded-xl font-bold text-sm transition-colors shadow-lg"
              >
                Check Eligibility <FaArrowRight className="ml-2" />
              </Link>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default GLP1Contact;
